import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';

import useClickOutside from '../../hooks/useClickOutside.js';

import LdsButton from '../LdsButton/index.jsx';
import LdsIcon from '../LdsIcon/index.jsx';

import Phone from './Phone.jsx';
import Links from './Links.jsx';
import Chat from './Chat.jsx';
import Content from './Content.jsx';

export default function LdsContactLilly({
  btnAriaLabelOpened = 'Close Contact Us',
  btnAriaLabelClosed = 'Open Contact Us',
  btnClasses = 'system',
  btnCloseAriaLabel = 'Close Contact Us',
  btnText = 'Contact Us',
  chatText = 'Click to Chat',
  classes,
  forceMobile = false,
  introText = 'We’re here to help.',
  isOpenInit = false,
  onChat = () => {},
  onCollapse = () => {},
  onExpand = () => {},
  onQuestion = () => {},
  options,
  phoneText = 'Call:',
  closeOnDocumentClick = true,
  icon,
  clearBtnClasses = false,
  ...rest
}) {
  const [isOpen, setIsOpen] = useState(isOpenInit);
  const [height, setHeight] = useState(0);
  const dropdownRef = useRef(null);
  const ref = useClickOutside((e) => {
    if (isOpen && closeOnDocumentClick) {
      setIsOpen(false);
      onCollapse(e);
    }
  });
  const toggleOpen = (e) => {
    e.preventDefault();
    if (isOpen) {
      onCollapse(e);
    } else {
      onExpand(e);
    }
    setIsOpen(!isOpen);
  };

  const handleChatClick = (e) => {
    e.preventDefault();
    onChat(e);
  };

  const handleQuestionClick = (e) => {
    onQuestion(e);
  };

  useEffect(() => {
    if (isOpen) {
      setHeight(`${dropdownRef.current.scrollHeight + 24}px`);
    } else {
      setHeight('0px');
    }
  }, [isOpen, dropdownRef]);

  return (
    <div
      className={cn(
        'lds-contact-lilly',
        {
          'force-mobile': forceMobile,
          'lds-contact-lilly-active': isOpen,
          [`${classes}`]: classes,
        }
      )}
      ref={ref}
      {...rest}
    >
      <LdsButton
        aria-expanded={isOpen}
        aria-label={isOpen ? btnAriaLabelOpened : btnAriaLabelClosed}
        clearDefaultClasses={clearBtnClasses}
        classes={cn(
          'lds-contact-lilly-toggle',
          {
            [`${btnClasses}`]: btnClasses,
          }
        )}
        onClick={toggleOpen}
      >
        {icon && <LdsIcon name={icon} inline decorative className="lds-contact-lilly-toggle-icon" />}
        {btnText}
      </LdsButton>

      <div
        className="lds-contact-lilly-dropdown"
        ref={dropdownRef}
        style={{
          maxHeight: height,
          visibility: isOpen ? 'visible' : 'hidden',
        }}
        aria-hidden={!isOpen}
      >
        <LdsButton
          aria-label={btnCloseAriaLabel}
          classes={cn(
            'lds-contact-lilly-close',
            {
              [`${btnClasses}`]: btnClasses,
            }
          )}
          icon="X"
          iconOnly
          onClick={toggleOpen}
        />
        <div className="lds-contact-lilly-dropdown-top">
          <div className="lds-contact-lilly-logo">
            <lds-ask-lilly-logo></lds-ask-lilly-logo>
          </div>
          <div className="lds-contact-lilly-dropdown-intro">{introText}</div>
        </div>

        <div className="lds-contact-lilly-dropdown-bottom">

          {options.phone
            && <Phone
              phone={options.phone}
              phoneText={phoneText}
            />
          }

          {options.hcpLinks
            && <Links
              links={options.hcpLinks}
              linkType="hcp"
            />
          }

          {options.questionLinks
            && <Links
              links={options.questionLinks}
              linkType="question"
              onQuestionClick={handleQuestionClick}
            />
          }

          {options.enableChat
            && <Chat
              chatText={chatText}
              onChatClick={handleChatClick}
            />
          }

          {options.content
            && <Content
              content={options.content}
            />
          }

        </div>
      </div>
    </div>
  );
}

LdsContactLilly.propTypes = {
  btnAriaLabelOpened: PropTypes.string,
  btnAriaLabelClosed: PropTypes.string,
  btnClasses: PropTypes.string,
  btnCloseAriaLabel: PropTypes.string,
  chatText: PropTypes.string,
  classes: PropTypes.string,
  forceMobile: PropTypes.bool,
  introText: PropTypes.string,
  isOpenInit: PropTypes.bool,
  onChat: PropTypes.func,
  onCollapse: PropTypes.func,
  onExpand: PropTypes.func,
  onQuestion: PropTypes.func,
  options: PropTypes.object.isRequired,
  phoneText: PropTypes.string,
};
